import { Navigate, createBrowserRouter, useParams } from 'react-router';
import { invariant } from '@/lib/invariant';
import { RouteError } from '@/pages/route-error';
import { PATHS, ROUTE_PATTERNS } from '@/routes/paths';
import { RootLayout } from '@/layouts/router/root-layout';
import { RequireAnonymousLayout } from '@/layouts/router/require-anonymous-layout';
import { RequireAuthLayout } from '@/layouts/router/require-auth-layout';
import { RequireOrganizationLayout } from '@/layouts/router/require-organization-layout';
import { AuthLayout } from '@/layouts/app/auth-layout';
import { OnboardingLayout } from '@/layouts/app/onboarding-layout';
import { DashboardLayout } from '@/layouts/app/dashboard-layout';
import { StatusLayout } from '@/layouts/app/status-layout';
import { PropertyTabbedLayout } from '@/layouts/app/property-tabbed-layout';

function NavigateToPropertyUnits(): React.ReactElement {
  const { propertyId } = useParams();
  invariant(propertyId);
  return <Navigate to={PATHS.propertyUnits(propertyId)} replace />;
}

export const router = createBrowserRouter([
  {
    id: 'root',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireAuthLayout />,
        children: [
          {
            element: <RequireOrganizationLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={PATHS.dashboard} replace />,
              },
              {
                path: ROUTE_PATTERNS.dashboard,
                element: <Navigate to={PATHS.properties} replace />,
              },
              {
                element: <DashboardLayout />,
                children: [
                  {
                    path: ROUTE_PATTERNS.members,
                    lazy: {
                      Component: () => import('@/pages/dashboard/members').then((module) => module.Members),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.member,
                    lazy: {
                      Component: () =>
                        import('@/pages/dashboard/members/[user-id]/index').then((module) => module.Member),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.addMember,
                    lazy: {
                      Component: () => import('@/pages/dashboard/members/add').then((module) => module.AddMember),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.properties,
                    lazy: {
                      Component: () => import('@/pages/dashboard/properties').then((module) => module.Properties),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.addProperty,
                    lazy: {
                      Component: () => import('@/pages/dashboard/properties/add').then((module) => module.AddProperty),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.updateProperty,
                    lazy: {
                      Component: () =>
                        import('@/pages/dashboard/properties/[property-id]/update').then(
                          (module) => module.UpdateProperty,
                        ),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.addPropertyUnit,
                    lazy: {
                      Component: () =>
                        import('@/pages/dashboard/properties/[property-id]/units/add').then((module) => module.AddUnit),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.updatePropertyUnit,
                    lazy: {
                      Component: () =>
                        import('@/pages/dashboard/properties/[property-id]/units/[unit-id]/update').then(
                          (module) => module.UpdateUnit,
                        ),
                    },
                  },
                  {
                    path: ROUTE_PATTERNS.property,
                    element: <PropertyTabbedLayout />,
                    children: [
                      {
                        index: true,
                        element: <NavigateToPropertyUnits />,
                      },
                      {
                        path: ROUTE_PATTERNS.propertyUnits,
                        lazy: {
                          Component: () =>
                            import('@/pages/dashboard/properties/[property-id]/units/index').then(
                              (module) => module.Units,
                            ),
                        },
                      },
                      {
                        path: ROUTE_PATTERNS.propertyNotes,
                        lazy: {
                          Component: () =>
                            import('@/pages/dashboard/properties/[property-id]/notes').then((module) => module.Notes),
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            element: <OnboardingLayout />,
            children: [
              {
                path: ROUTE_PATTERNS.selectOrganization,
                lazy: {
                  Component: () =>
                    import('@/pages/onboarding/select-organization').then((module) => module.SelectOrganization),
                },
              },
              {
                path: ROUTE_PATTERNS.createOrganization,
                lazy: {
                  Component: () =>
                    import('@/pages/onboarding/create-organization').then((module) => module.CreateOrganization),
                },
              },
            ],
          },
        ],
      },
      {
        element: <RequireAnonymousLayout />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: ROUTE_PATTERNS.signIn,
                lazy: {
                  Component: () => import('@/pages/auth/sign-in').then((module) => module.SignIn),
                },
              },
              {
                path: ROUTE_PATTERNS.signUp,
                lazy: {
                  Component: () => import('@/pages/auth/sign-up').then((module) => module.SignUp),
                },
              },
            ],
          },
        ],
      },
      {
        element: <StatusLayout />,
        children: [
          {
            path: ROUTE_PATTERNS.health,
            lazy: {
              Component: () => import('@/pages/health').then((module) => module.Health),
            },
          },
        ],
      },
      {
        path: '*',
        lazy: {
          Component: () => import('@/pages/not-found').then((module) => module.NotFound),
        },
      },
    ],
  },
]);
